import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface DealPerformance {
  id: string
  category: string
  deals_won: number
  deals_lost: number
  win_rate: number
  avg_deal_value: number
  total_revenue: number
  avg_sales_cycle: number
  trend: string
}

interface WinLossReason {
  id: string
  reason: string
  win_count: number
  loss_count: number
  win_percentage: number
  deal_value_impacted: number
  recommended_action: string
}

interface CompetitorWin {
  id: string
  competitor: string
  wins_against: number
  losses_to: number
  win_rate_vs: number
  avg_discount_given: number
  key_differentiator: string
}

interface Statistics {
  total_deals_closed: number
  overall_win_rate: number
  avg_deal_size: number
  total_revenue_won: number
  avg_discount: number
  competitive_win_rate: number
}

export default function DealAnalyticsPage() {
  const [performance, setPerformance] = useState<DealPerformance[]>([])
  const [winLoss, setWinLoss] = useState<WinLossReason[]>([])
  const [competitorWins, setCompetitorWins] = useState<CompetitorWin[]>([])
  const [statistics, setStatistics] = useState<Statistics | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      const [perfRes, winLossRes, compRes, statsRes] = await Promise.all([
        fetch('/api/dealanalytics/performance'),
        fetch('/api/dealanalytics/winloss'),
        fetch('/api/dealanalytics/competitors'),
        fetch('/api/dealanalytics/statistics')
      ])
      if (perfRes.ok) setPerformance(await perfRes.json())
      if (winLossRes.ok) setWinLoss(await winLossRes.json())
      if (compRes.ok) setCompetitorWins(await compRes.json())
      if (statsRes.ok) setStatistics(await statsRes.json())
    } catch (error) {
      console.error('Error fetching deal analytics data:', error)
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value)
  }

  const getTrendColor = (trend: string) => {
    if (trend === 'improving') return 'text-green-600'
    if (trend === 'declining') return 'text-red-600'
    return 'text-gray-600'
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Deal Analytics</h1>
          <p className="mt-2 text-gray-600">Phase 234 - Deal performance and win/loss analysis</p>
        </div>

        {statistics && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Deals Closed</p>
              <p className="text-2xl font-bold text-gray-900">{statistics.total_deals_closed}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Win Rate</p>
              <p className="text-2xl font-bold text-green-600">{statistics.overall_win_rate}%</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Avg Deal Size</p>
              <p className="text-2xl font-bold text-blue-600">{formatCurrency(statistics.avg_deal_size)}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Revenue Won</p>
              <p className="text-2xl font-bold text-purple-600">{formatCurrency(statistics.total_revenue_won)}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Avg Discount</p>
              <p className="text-2xl font-bold text-orange-600">{statistics.avg_discount}%</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Competitive Win</p>
              <p className="text-2xl font-bold text-indigo-600">{statistics.competitive_win_rate}%</p>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow mb-6">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold">Performance by Category</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Won</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Lost</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Win Rate</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Avg Value</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Revenue</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Trend</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {performance.map((perf) => (
                  <tr key={perf.id}>
                    <td className="px-6 py-4 font-medium text-gray-900">{perf.category}</td>
                    <td className="px-6 py-4 text-green-600">{perf.deals_won}</td>
                    <td className="px-6 py-4 text-red-600">{perf.deals_lost}</td>
                    <td className="px-6 py-4 font-bold text-blue-600">{perf.win_rate}%</td>
                    <td className="px-6 py-4 text-gray-500">{formatCurrency(perf.avg_deal_value)}</td>
                    <td className="px-6 py-4 text-purple-600">{formatCurrency(perf.total_revenue)}</td>
                    <td className={`px-6 py-4 ${getTrendColor(perf.trend)}`}>{perf.trend}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b">
              <h2 className="text-lg font-semibold">Win/Loss Reasons</h2>
            </div>
            <div className="p-6 space-y-4">
              {winLoss.map((reason) => (
                <div key={reason.id} className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-gray-900">{reason.reason}</span>
                    <span className="text-lg font-bold text-blue-600">{reason.win_percentage}%</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm mb-2">
                    <div className="text-center p-2 bg-white rounded">
                      <p className="text-gray-500">Wins</p>
                      <p className="font-medium text-green-600">{reason.win_count}</p>
                    </div>
                    <div className="text-center p-2 bg-white rounded">
                      <p className="text-gray-500">Losses</p>
                      <p className="font-medium text-red-600">{reason.loss_count}</p>
                    </div>
                  </div>
                  <p className="text-sm text-purple-600">{reason.recommended_action}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b">
              <h2 className="text-lg font-semibold">Competitive Analysis</h2>
            </div>
            <div className="p-6 space-y-4">
              {competitorWins.map((comp) => (
                <div key={comp.id} className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-gray-900">{comp.competitor}</span>
                    <span className="text-lg font-bold text-green-600">{comp.win_rate_vs}% win rate</span>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-sm">
                    <div className="text-center p-2 bg-white rounded">
                      <p className="text-gray-500">Wins</p>
                      <p className="font-medium text-green-600">{comp.wins_against}</p>
                    </div>
                    <div className="text-center p-2 bg-white rounded">
                      <p className="text-gray-500">Losses</p>
                      <p className="font-medium text-red-600">{comp.losses_to}</p>
                    </div>
                    <div className="text-center p-2 bg-white rounded">
                      <p className="text-gray-500">Discount</p>
                      <p className="font-medium text-orange-600">{comp.avg_discount_given}%</p>
                    </div>
                  </div>
                  <p className="mt-2 text-sm text-gray-500">Key: <span className="text-purple-600">{comp.key_differentiator}</span></p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
